import React, { useMemo, useState } from "react";
import { Col, Empty, Row, Segmented, Table } from "antd";
import ReactECharts from "echarts-for-react";
import {
  CHART_COLORS,
  ExecutionMonthColumn,
  fmtINRCompact,
  fmtINRSigned,
  fmtMonth,
  fmtMonthLong,
  num,
} from "./executionShared";
import {
  AXIS,
  CLICKABLE,
  Insight,
  InsightsRail,
  LEGEND,
  MONEY_AXIS,
  SeriesToggles,
  TOOLTIP,
  moneyCell,
  mutedDash,
  seriesClickIndex,
  signedCell,
  trendOf,
} from "./executionChartKit";
import {
  ActivityRollupRow,
  DrillHeader,
  MonthDetailItems,
  OutflowMonthDrill,
  OutflowPillar,
  TargetExecutedMonthDrill,
  tabForPillar,
  useMonthDrillData,
} from "./ExecutionMonthDrill";

// The two "detailed analysis" panels behind the Monthly Execution charts.
// The dashboard cards answer "are we on track?"; these answer "why?" — bigger
// chart, series toggles, month-by-month table and a computed (never generic)
// insights rail. All money series share ONE axis (₹) — no dual axes.
//
// They are PANELS, not modals: AnalysisPage hosts them as two tabs on a real
// route, so the view (which chart, which month, which measure) lives in the URL
// and the browser back button works. Each panel is also the drill surface —
// clicking a month swaps the all-months view for that month's composition (see
// ExecutionMonthDrill). drillMonth is controlled by the page so it can round-
// trip through the query string.

export interface AnalysisPanelProps {
  months: ExecutionMonthColumn[];
  projectId?: number | string;
  phaseId?: number;
  activityId?: number;
  /** Drilled month ('YYYY-MM-01'), or null for the all-months view. */
  drillMonth: string | null;
  onDrillMonthChange: (month: string | null) => void;
  /** Route out to the full Month Detail page. */
  onOpenMonthDetail?: (month: string, tab: string) => void;
}

// ==================================================================== chart 1

export const TargetExecutedAnalysis: React.FC<AnalysisPanelProps> = ({
  months,
  projectId,
  phaseId,
  activityId,
  drillMonth,
  onDrillMonthChange,
  onOpenMonthDetail,
}) => {
  const [series, setSeries] = useState<string[]>(["target", "executed", "backlog"]);
  const setDrillMonth = onDrillMonthChange;

  const drilledColumn = useMemo(
    () => months.find((m) => m.month === drillMonth) ?? null,
    [months, drillMonth]
  );
  const { data: rollup, loading: drillLoading, error: drillError } = useMonthDrillData(
    projectId,
    drilledColumn ? drillMonth : null,
    { phaseId, activityId },
    "activities",
    months
  );

  const actualized = useMemo(() => months.filter((m) => m.isActualized), [months]);
  const labels = useMemo(() => months.map((m) => fmtMonth(m.month)), [months]);
  const mask = (m: ExecutionMonthColumn, value: number) => (m.isActualized ? value : null);

  const chartOption = useMemo(() => {
    const all: any[] = [];
    if (series.includes("target")) {
      all.push({
        name: "Planned Expenditure",
        type: "bar",
        data: months.map((m) => num(m.targetAmount)),
        itemStyle: { color: CHART_COLORS.plan, borderRadius: [3, 3, 0, 0], ...CLICKABLE },
        barMaxWidth: 30,
      });
    }
    if (series.includes("executed")) {
      all.push({
        name: "Executed Expenditure",
        type: "bar",
        data: months.map((m) => mask(m, num(m.executedAmount))),
        itemStyle: { color: CHART_COLORS.actual, borderRadius: [3, 3, 0, 0], ...CLICKABLE },
        barMaxWidth: 30,
      });
    }
    if (series.includes("clientPlan")) {
      all.push({
        name: "Planned Revenue",
        type: "line",
        data: months.map((m) => num(m.clientPlanAmount) || null),
        itemStyle: { color: CHART_COLORS.clientPlan, ...CLICKABLE },
        lineStyle: { type: "dashed", width: 1.5, color: CHART_COLORS.clientPlan },
        symbol: "circle",
        symbolSize: 5,
      });
    }
    if (series.includes("backlog")) {
      all.push({
        name: "Closing Backlog",
        type: "line",
        data: months.map((m) => mask(m, num(m.closingBacklog))),
        itemStyle: { color: CHART_COLORS.backlog, ...CLICKABLE },
        lineStyle: { width: 1.5, color: CHART_COLORS.backlog },
        symbol: "diamond",
        symbolSize: 7,
      });
    }
    return {
      tooltip: TOOLTIP,
      legend: LEGEND,
      grid: { left: 66, right: 20, top: 20, bottom: 46 },
      xAxis: { type: "category", data: labels, ...AXIS },
      yAxis: MONEY_AXIS,
      series: all,
    };
  }, [months, labels, series]);

  const insights = useMemo((): Insight[] => {
    const list: Insight[] = [];
    if (!actualized.length) {
      list.push({
        title: "No Actuals Yet",
        body: "Capture monthly actuals per task to light up execution, variance and backlog.",
        tone: "neutral",
      });
    } else {
      const target = actualized.reduce((a, m) => a + num(m.targetAmount), 0);
      const executed = actualized.reduce((a, m) => a + num(m.executedAmount), 0);
      const pct = target > 0 ? (executed / target) * 100 : 0;
      list.push({
        title: pct >= 99.5 ? "Execution on Track" : pct >= 90 ? "Execution Slightly Behind" : "Execution Behind Plan",
        body: (
          <>
            {fmtINRCompact(executed)} executed against {fmtINRCompact(target)} planned (
            {pct.toFixed(1)}%).
          </>
        ),
        tone: pct >= 99.5 ? "ok" : "warn",
      });

      const last = actualized[actualized.length - 1];
      const prev = actualized[actualized.length - 2];
      const closing = num(last.closingBacklog);
      if (Math.abs(closing) < 1) {
        list.push({
          title: "No Carry-over Risk",
          body: <>Closing backlog is currently {fmtINRCompact(0)}.</>,
          tone: "ok",
        });
      } else {
        const movement = prev ? closing - num(prev.closingBacklog) : closing;
        list.push({
          title: closing > 0 ? "Carry-over Backlog" : "Ahead of Plan",
          body: (
            <>
              {fmtINRSigned(closing)} closing backlog in {fmtMonth(last.month)}
              {prev ? (
                <> ({movement >= 0 ? "up" : "down"} {fmtINRCompact(Math.abs(movement))} vs {fmtMonth(prev.month)})</>
              ) : null}
              .
            </>
          ),
          tone: closing > 0 ? "warn" : "ok",
        });
      }

      const worst = actualized
        .filter((m) => num(m.targetAmount) > 0)
        .reduce(
          (best: ExecutionMonthColumn | null, m) =>
            !best || num(m.monthVariance) < num(best.monthVariance) ? m : best,
          null
        );
      if (worst && num(worst.monthVariance) < -1) {
        list.push({
          title: "Needs Attention",
          body: (
            <>
              {fmtMonthLong(worst.month)} has the largest shortfall:{" "}
              {fmtINRSigned(num(worst.monthVariance))} vs plan.
            </>
          ),
          tone: "warn",
        });
      }

      const pcts = actualized
        .filter((m) => num(m.targetAmount) > 0)
        .map((m) => num(m.executedAmount) / num(m.targetAmount));
      const trend = trendOf(pcts);
      if (trend !== "flat") {
        list.push({
          title: trend === "up" ? "Execution Trending Up" : "Execution Trending Down",
          body: (
            <>
              Monthly execution % is {trend === "up" ? "improving" : "declining"} across the
              actualized window.
            </>
          ),
          tone: trend === "up" ? "ok" : "warn",
        });
      }
    }

    const peak = months.reduce(
      (best: ExecutionMonthColumn | null, m) =>
        !best || num(m.targetAmount) > num(best.targetAmount) ? m : best,
      null
    );
    if (peak && num(peak.targetAmount) > 0) {
      list.push({
        title: "Peak Execution Plan",
        body: (
          <>
            {fmtMonthLong(peak.month)} carries the highest planned expenditure at{" "}
            {fmtINRCompact(num(peak.targetAmount))}.
          </>
        ),
        tone: "neutral",
      });
    }
    return list.slice(0, 6);
  }, [months, actualized]);

  const tableRows = useMemo(() => {
    let cumulative = 0;
    let prevBacklog = 0;
    return months.map((m) => {
      const executed = m.isActualized ? num(m.executedAmount) : null;
      if (executed !== null) cumulative += executed;
      const closing = m.isActualized ? num(m.closingBacklog) : null;
      const movement = closing === null ? null : closing - prevBacklog;
      if (closing !== null) prevBacklog = closing;
      const target = num(m.targetAmount);
      return {
        key: m.month,
        month: m.month,
        isActualized: m.isActualized,
        target,
        executed,
        pct: executed !== null && target > 0 ? (executed / target) * 100 : null,
        variance: m.isActualized ? num(m.monthVariance) : null,
        clientPlan: num(m.clientPlanAmount),
        clientPlanIsDerived: m.clientPlanIsDerived !== false,
        closing,
        movement,
        cumulative: m.isActualized ? cumulative : null,
      };
    });
  }, [months]);

  return (
    <>
      {!months.length ? (
        <Empty description="No months in the selected window" style={{ padding: "48px 0" }} />
      ) : drilledColumn ? (
        <>
          <DrillHeader
            month={drilledColumn.month}
            caption="carry-over bridge and activity-wise contribution"
            onBack={() => setDrillMonth(null)}
            onOpenMonthDetail={
              onOpenMonthDetail
                ? () => onOpenMonthDetail(drilledColumn.month, "tasks")
                : undefined
            }
          />
          <TargetExecutedMonthDrill
            month={drilledColumn}
            rows={(rollup as ActivityRollupRow[]) || []}
            loading={drillLoading}
            error={drillError}
          />
        </>
      ) : (
        <>
          <div style={{ marginBottom: 12 }}>
            <SeriesToggles
              options={[
                { key: "target", label: "Planned Exp.", color: CHART_COLORS.plan },
                { key: "executed", label: "Executed Exp.", color: CHART_COLORS.actual },
                { key: "clientPlan", label: "Planned Revenue", color: CHART_COLORS.clientPlan },
                { key: "backlog", label: "Backlog", color: CHART_COLORS.backlog },
              ]}
              value={series}
              onChange={(next) => next.length && setSeries(next)}
            />
          </div>
          <Row gutter={[16, 16]}>
            <Col xs={24} xl={17}>
              <ReactECharts
                option={chartOption}
                style={{ height: 380 }}
                notMerge
                onEvents={{
                  click: (params: any) => {
                    const index = seriesClickIndex(params);
                    if (index !== null && months[index]) setDrillMonth(months[index].month);
                  },
                }}
              />
              <div style={{ fontSize: 11.5, color: "#a9b6c9", textAlign: "center" }}>
                Click a month to drill into its carry-over bridge and activities
              </div>
            </Col>
            <Col xs={24} xl={7}>
              <InsightsRail insights={insights} />
            </Col>
          </Row>
          <Table
            size="small"
            style={{ marginTop: 16 }}
            dataSource={tableRows}
            pagination={false}
            scroll={{ x: 1080, y: 260 }}
            columns={[
              {
                title: "Month",
                dataIndex: "month",
                width: 90,
                fixed: "left",
                render: (value: string, row: any) => (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => setDrillMonth(value)}
                    onKeyDown={(event) => event.key === "Enter" && setDrillMonth(value)}
                    style={{
                      fontWeight: 600,
                      color: row.isActualized ? "#022c57" : "#a9b6c9",
                      cursor: "pointer",
                      textDecoration: "underline dotted #b6c2d9",
                      textUnderlineOffset: 3,
                    }}
                  >
                    {fmtMonth(value)}
                  </span>
                ),
              },
              { title: "Planned Exp.", dataIndex: "target", align: "right", width: 120, render: (v: number) => moneyCell(v) },
              {
                title: "Executed Exp.", dataIndex: "executed", align: "right", width: 125,
                render: (v: number | null) => (v === null ? mutedDash : moneyCell(v, true)),
              },
              {
                title: "Execution %", dataIndex: "pct", align: "right", width: 100,
                render: (v: number | null) =>
                  v === null ? mutedDash : (
                    <span style={{ fontVariantNumeric: "tabular-nums", color: v >= 99.5 ? CHART_COLORS.positive : "#526484" }}>
                      {v.toFixed(1)}%
                    </span>
                  ),
              },
              {
                title: "Variance", dataIndex: "variance", align: "right", width: 110,
                render: (v: number | null) => (v === null ? mutedDash : signedCell(v, true)),
              },
              {
                title: "Planned Revenue", dataIndex: "clientPlan", align: "right", width: 130,
                // An entered 0.00 is a real plan and must read as ₹0, not as "no plan".
                render: (v: number, row: any) =>
                  v || !row.clientPlanIsDerived ? moneyCell(v) : mutedDash,
              },
              {
                title: "Closing Backlog", dataIndex: "closing", align: "right", width: 120,
                render: (v: number | null) => (v === null ? mutedDash : signedCell(v)),
              },
              {
                title: "Backlog Δ", dataIndex: "movement", align: "right", width: 110,
                render: (v: number | null) => (v === null ? mutedDash : signedCell(v)),
              },
              {
                title: "Cumulative Executed", dataIndex: "cumulative", align: "right", width: 140,
                render: (v: number | null) => (v === null ? mutedDash : moneyCell(v)),
              },
            ]}
          />
        </>
      )}
    </>
  );
};

// ==================================================================== chart 2

export type OutflowMeasure = "total" | "material" | "manpower" | "machinery";

export const MEASURE_FIELDS: Record<
  OutflowMeasure,
  { planned: keyof ExecutionMonthColumn; earned: keyof ExecutionMonthColumn; label: string }
> = {
  total: { planned: "plannedOutflow", earned: "earnedOutflow", label: "Total" },
  material: { planned: "plannedMaterial", earned: "earnedMaterial", label: "Material" },
  manpower: { planned: "plannedManpower", earned: "earnedManpower", label: "Manpower" },
  machinery: { planned: "plannedMachinery", earned: "earnedMachinery", label: "Machinery" },
};

export const ThreeMOutflowAnalysis: React.FC<
  AnalysisPanelProps & {
    measure: OutflowMeasure;
    onMeasureChange: (measure: OutflowMeasure) => void;
  }
> = ({
  months,
  measure,
  onMeasureChange,
  projectId,
  phaseId,
  activityId,
  drillMonth,
  onDrillMonthChange,
  onOpenMonthDetail,
}) => {
  const [mode, setMode] = useState<"Monthly" | "Cumulative">("Monthly");
  const setMeasure = onMeasureChange;
  const setDrillMonth = onDrillMonthChange;

  const drilledColumn = useMemo(
    () => months.find((m) => m.month === drillMonth) ?? null,
    [months, drillMonth]
  );
  const { data: items, loading: drillLoading, error: drillError } = useMonthDrillData(
    projectId,
    drilledColumn ? drillMonth : null,
    { phaseId, activityId },
    "items",
    months
  );

  const fields = MEASURE_FIELDS[measure];
  const actualized = useMemo(() => months.filter((m) => m.isActualized), [months]);
  const labels = useMemo(() => months.map((m) => fmtMonth(m.month)), [months]);

  const chartOption = useMemo(() => {
    const plannedMonthly = months.map((m) => num(m[fields.planned] as string));
    const earnedMonthly = months.map((m) =>
      m.isActualized ? num(m[fields.earned] as string) : null
    );
    let series: any[];
    if (mode === "Monthly") {
      series = [
        {
          name: `${fields.label} Planned`,
          type: "bar",
          data: plannedMonthly,
          itemStyle: { color: CHART_COLORS.plan, borderRadius: [3, 3, 0, 0], ...CLICKABLE },
          barMaxWidth: 30,
        },
        {
          name: `${fields.label} Earned`,
          type: "bar",
          data: earnedMonthly,
          itemStyle: { color: CHART_COLORS.actual, borderRadius: [3, 3, 0, 0], ...CLICKABLE },
          barMaxWidth: 30,
        },
      ];
    } else {
      let planned = 0;
      let earned = 0;
      const plannedCumulative = plannedMonthly.map((v) => (planned += v));
      const earnedCumulative = earnedMonthly.map((v) =>
        v === null ? null : (earned += v)
      );
      series = [
        {
          name: `${fields.label} Planned (cumulative)`,
          type: "line",
          data: plannedCumulative,
          itemStyle: { color: CHART_COLORS.plan, ...CLICKABLE },
          lineStyle: { width: 2, color: CHART_COLORS.plan },
          areaStyle: { color: CHART_COLORS.plan, opacity: 0.07 },
          symbol: "circle",
          symbolSize: 5,
        },
        {
          name: `${fields.label} Earned (cumulative)`,
          type: "line",
          data: earnedCumulative,
          itemStyle: { color: CHART_COLORS.actual, ...CLICKABLE },
          lineStyle: { width: 2, color: CHART_COLORS.actual },
          areaStyle: { color: CHART_COLORS.actual, opacity: 0.07 },
          symbol: "circle",
          symbolSize: 5,
        },
      ];
    }
    return {
      tooltip: TOOLTIP,
      legend: LEGEND,
      grid: { left: 66, right: 20, top: 20, bottom: 46 },
      xAxis: { type: "category", data: labels, ...AXIS },
      yAxis: MONEY_AXIS,
      series,
    };
  }, [months, labels, fields, mode]);

  const insights = useMemo((): Insight[] => {
    const list: Insight[] = [];
    const plannedAll = actualized.reduce((a, m) => a + num(m[fields.planned] as string), 0);
    const earnedAll = actualized.reduce((a, m) => a + num(m[fields.earned] as string), 0);
    if (!actualized.length) {
      return [
        {
          title: "No Actuals Yet",
          body: "Executed expenditure appears once monthly actuals are captured.",
          tone: "neutral",
        },
      ];
    }
    const pct = plannedAll > 0 ? (earnedAll / plannedAll) * 100 : 0;
    list.push({
      title: `${fields.label} Earned Performance`,
      body: (
        <>
          {fmtINRCompact(earnedAll)} earned against {fmtINRCompact(plannedAll)} planned (
          {pct.toFixed(1)}%).
        </>
      ),
      tone: pct >= 99.5 ? "ok" : "warn",
    });

    const gap = actualized.reduce(
      (best: ExecutionMonthColumn | null, m) => {
        const dev = Math.abs(num(m[fields.earned] as string) - num(m[fields.planned] as string));
        const bestDev = best
          ? Math.abs(num(best[fields.earned] as string) - num(best[fields.planned] as string))
          : -1;
        return dev > bestDev ? m : best;
      },
      null
    );
    if (gap) {
      const dev = num(gap[fields.earned] as string) - num(gap[fields.planned] as string);
      if (Math.abs(dev) > 1) {
        list.push({
          title: "Largest Gap",
          body: (
            <>
              {fmtMonthLong(gap.month)} has the largest planned-vs-executed variance:{" "}
              {fmtINRSigned(dev)}.
            </>
          ),
          tone: "warn",
        });
      } else {
        list.push({
          title: "No Material Gaps",
          body: <>Executed tracks planned within {fmtINRCompact(1)} in every actualized month.</>,
          tone: "ok",
        });
      }
    }

    const peak = months.reduce(
      (best: ExecutionMonthColumn | null, m) =>
        !best || num(m[fields.planned] as string) > num(best[fields.planned] as string) ? m : best,
      null
    );
    if (peak && num(peak[fields.planned] as string) > 0) {
      list.push({
        title: "Highest Expenditure Month",
        body: (
          <>
            {fmtMonthLong(peak.month)} at {fmtINRCompact(num(peak[fields.planned] as string))}{" "}
            planned.
          </>
        ),
        tone: "neutral",
      });
    }

    if (measure === "total") {
      const contributions = (["material", "manpower", "machinery"] as const).map((key) => ({
        key,
        label: MEASURE_FIELDS[key].label,
        value: months.reduce((a, m) => a + num(m[MEASURE_FIELDS[key].planned] as string), 0),
      }));
      const totalPlanned = contributions.reduce((a, c) => a + c.value, 0);
      if (totalPlanned > 0) {
        const sorted = [...contributions].sort((a, b) => b.value - a.value);
        list.push({
          title: "Primary Cost Driver",
          body: (
            <>
              {sorted[0].label} leads at {((sorted[0].value / totalPlanned) * 100).toFixed(0)}%
              {" — "}
              {sorted
                .map((c) => `${c.label} ${((c.value / totalPlanned) * 100).toFixed(0)}%`)
                .join(" · ")}
              .
            </>
          ),
          tone: "neutral",
        });
      }
    }

    const trend = trendOf(months.map((m) => num(m[fields.planned] as string)));
    if (trend !== "flat") {
      list.push({
        title: trend === "up" ? "Expenditure Ramping Up" : "Expenditure Tapering",
        body: (
          <>
            {fields.label} planned expenditure is {trend === "up" ? "increasing" : "decreasing"} across
            the window.
          </>
        ),
        tone: "neutral",
      });
    }
    return list.slice(0, 6);
  }, [months, actualized, fields, measure]);

  const tableRows = useMemo(() => {
    let plannedCumulative = 0;
    let earnedCumulative = 0;
    return months.map((m) => {
      const planned = num(m[fields.planned] as string);
      const earned = m.isActualized ? num(m[fields.earned] as string) : null;
      plannedCumulative += planned;
      if (earned !== null) earnedCumulative += earned;
      return {
        key: m.month,
        month: m.month,
        isActualized: m.isActualized,
        planned,
        earned,
        variance: earned === null ? null : earned - planned,
        pct: earned !== null && planned > 0 ? (earned / planned) * 100 : null,
        plannedCumulative,
        earnedCumulative: m.isActualized ? earnedCumulative : null,
      };
    });
  }, [months, fields]);

  return (
    <>
      {!months.length ? (
        <Empty description="No months in the selected window" style={{ padding: "48px 0" }} />
      ) : drilledColumn ? (
        <>
          <DrillHeader
            month={drilledColumn.month}
            caption={
              measure === "total"
                ? "3M split and top contributors"
                : `top ${MEASURE_FIELDS[measure].label.toLowerCase()} contributors`
            }
            onBack={() => setDrillMonth(null)}
            onOpenMonthDetail={
              onOpenMonthDetail
                ? () =>
                    onOpenMonthDetail(
                      drilledColumn.month,
                      tabForPillar(measure as OutflowPillar)
                    )
                : undefined
            }
          />
          {/* The measure segmented stays: it re-scopes the drill in place. */}
          <div style={{ marginBottom: 12 }}>
            <Segmented
              size="small"
              options={(["total", "material", "manpower", "machinery"] as const).map((key) => ({
                label: MEASURE_FIELDS[key].label,
                value: key,
              }))}
              value={measure}
              onChange={(value) => setMeasure(value as OutflowMeasure)}
            />
          </div>
          <OutflowMonthDrill
            month={drilledColumn}
            items={(items as MonthDetailItems) || null}
            pillar={measure as OutflowPillar}
            loading={drillLoading}
            error={drillError}
          />
        </>
      ) : (
        <>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
            <Segmented
              size="small"
              options={(["total", "material", "manpower", "machinery"] as const).map((key) => ({
                label: MEASURE_FIELDS[key].label,
                value: key,
              }))}
              value={measure}
              onChange={(value) => setMeasure(value as OutflowMeasure)}
            />
            <Segmented
              size="small"
              options={["Monthly", "Cumulative"]}
              value={mode}
              onChange={(value) => setMode(value as "Monthly" | "Cumulative")}
            />
          </div>
          <Row gutter={[16, 16]}>
            <Col xs={24} xl={17}>
              <ReactECharts
                option={chartOption}
                style={{ height: 380 }}
                notMerge
                onEvents={{
                  click: (params: any) => {
                    const index = seriesClickIndex(params);
                    if (index !== null && months[index]) setDrillMonth(months[index].month);
                  },
                }}
              />
              <div style={{ fontSize: 11.5, color: "#a9b6c9", textAlign: "center" }}>
                Click a month to drill into its 3M split and top contributors
              </div>
            </Col>
            <Col xs={24} xl={7}>
              <InsightsRail insights={insights} />
            </Col>
          </Row>
          <Table
            size="small"
            style={{ marginTop: 16 }}
            dataSource={tableRows}
            pagination={false}
            scroll={{ x: 920, y: 260 }}
            columns={[
              {
                title: "Month",
                dataIndex: "month",
                width: 90,
                fixed: "left",
                render: (value: string, row: any) => (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => setDrillMonth(value)}
                    onKeyDown={(event) => event.key === "Enter" && setDrillMonth(value)}
                    style={{
                      fontWeight: 600,
                      color: row.isActualized ? "#022c57" : "#a9b6c9",
                      cursor: "pointer",
                      textDecoration: "underline dotted #b6c2d9",
                      textUnderlineOffset: 3,
                    }}
                  >
                    {fmtMonth(value)}
                  </span>
                ),
              },
              { title: `${fields.label} Planned`, dataIndex: "planned", align: "right", width: 130, render: (v: number) => moneyCell(v) },
              {
                title: `${fields.label} Executed`, dataIndex: "earned", align: "right", width: 130,
                render: (v: number | null) => (v === null ? mutedDash : moneyCell(v, true)),
              },
              {
                title: "Variance", dataIndex: "variance", align: "right", width: 110,
                render: (v: number | null) => (v === null ? mutedDash : signedCell(v)),
              },
              {
                title: "Executed %", dataIndex: "pct", align: "right", width: 110,
                render: (v: number | null) =>
                  v === null ? mutedDash : (
                    <span style={{ fontVariantNumeric: "tabular-nums", color: "#526484" }}>
                      {v.toFixed(1)}%
                    </span>
                  ),
              },
              {
                title: "Cumulative Planned", dataIndex: "plannedCumulative", align: "right", width: 140,
                render: (v: number) => moneyCell(v),
              },
              {
                title: "Cumulative Executed", dataIndex: "earnedCumulative", align: "right", width: 150,
                render: (v: number | null) => (v === null ? mutedDash : moneyCell(v)),
              },
            ]}
          />
        </>
      )}
    </>
  );
};
